<script lang="ts">
  import { Consultorios, Liquidaciones, Tarifas, Seguros, CuentasConsultorio, AcuerdosClinica, Compartido,
    type Consultorio, type Profesional, type ReporteIngresosConsultorio, type Liquidacion,
    type Tarifa, type TarifaConvenio, type Aseguradora, type ReporteEstadoCuenta,
    type CuentaConsultorio, type AcuerdoClinica, type EstadoCuentaCompartido } from '../lib/api';

  let consultorios = $state<Consultorio[]>([]);
  let profesionales = $state<Profesional[]>([]);
  let error = $state<string|null>(null);
  let cargando = $state(false);

  let nuevoCons = $state<Consultorio>({ codigo:'', nombre:'', tipo:'PROPIO', vinculo:'INTERNO', modeloCobro:'COMISION', comisionHospitalPct:0, rentaMensual:0, activo:true });
  let nuevoProf = $state<Profesional>({ nombre:'', colegiado:'', nit:'', tipoRelacion:'EMPLEADO', honorarioPct:100, activo:true });
  let editandoConsId = $state<string | null>(null);
  let editandoProfId = $state<string | null>(null);

  let tab = $state('consultorios');

  // Reporte
  const hoy = new Date();
  const primero = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const iso = (d: Date) => d.toISOString().slice(0,10);
  let desde = $state(iso(primero));
  let hasta = $state(iso(hoy));
  let reporte = $state<ReporteIngresosConsultorio|null>(null);

  async function recargar() {
    cargando = true; error = null;
    try {
      consultorios = await Consultorios.list(true);
      profesionales = await Consultorios.profesionales(true);
    } catch (e: any) { error = e.message; }
    cargando = false;
  }

  function editarConsultorio(c: Consultorio) {
    editandoConsId = c.id ?? null;
    nuevoCons = { codigo: c.codigo, nombre: c.nombre, tipo: c.tipo, vinculo: c.vinculo, modeloCobro: c.modeloCobro ?? 'COMISION', comisionHospitalPct: c.comisionHospitalPct ?? 0, rentaMensual: c.rentaMensual ?? 0, activo: c.activo ?? true, tenantExternoId: c.tenantExternoId, medicoResponsableId: c.medicoResponsableId };
  }

  function editarProfesional(p: Profesional) {
    editandoProfId = p.id ?? null;
    nuevoProf = { nombre: p.nombre, colegiado: p.colegiado ?? '', nit: p.nit ?? '', tipoRelacion: p.tipoRelacion, honorarioPct: p.honorarioPct ?? 100, activo: p.activo ?? true };
  }

  function cancelarConsultorio() {
    editandoConsId = null;
    nuevoCons = { codigo:'', nombre:'', tipo:'PROPIO', vinculo:'INTERNO', modeloCobro:'COMISION', comisionHospitalPct:0, rentaMensual:0, activo:true };
  }

  function cancelarProfesional() {
    editandoProfId = null;
    nuevoProf = { nombre:'', colegiado:'', nit:'', tipoRelacion:'EMPLEADO', honorarioPct:100, activo:true };
  }

  async function guardarConsultorio() {
    error = null;
    if (!nuevoCons.codigo || !nuevoCons.nombre) { error = 'Código y nombre del consultorio son requeridos'; return; }
    try {
      const body: Consultorio = { ...nuevoCons };
      if (body.tipo === 'PROPIO') { body.modeloCobro = null; body.comisionHospitalPct = 0; body.rentaMensual = 0; }
      if (body.vinculo !== 'EXTERNO') { body.tenantExternoId = null; }
      if (editandoConsId) {
        await Consultorios.actualizar(editandoConsId, body);
      } else {
        await Consultorios.crear(body);
      }
      nuevoCons = { codigo:'', nombre:'', tipo:'PROPIO', vinculo:'INTERNO', modeloCobro:'COMISION', comisionHospitalPct:0, rentaMensual:0, activo:true };
      editandoConsId = null;
      await recargar();
    } catch (e: any) { error = e.message; }
  }

  async function eliminarConsultorio(c: Consultorio) {
    if (!confirm(`¿Eliminar el consultorio ${c.nombre}?`)) return;
    try { await Consultorios.eliminar(c.id!); await recargar(); }
    catch (e: any) { error = e.message; }
  }

  async function guardarProfesional() {
    error = null;
    if (!nuevoProf.nombre) { error = 'Nombre del profesional requerido'; return; }
    try {
      if (editandoProfId) {
        await Consultorios.actualizarProf(editandoProfId, { ...nuevoProf });
      } else {
        await Consultorios.crearProf({ ...nuevoProf });
      }
      nuevoProf = { nombre:'', colegiado:'', nit:'', tipoRelacion:'EMPLEADO', honorarioPct:100, activo:true };
      editandoProfId = null;
      await recargar();
    } catch (e: any) { error = e.message; }
  }

  async function eliminarProfesional(p: Profesional) {
    if (!confirm(`¿Eliminar a ${p.nombre}?`)) return;
    try { await Consultorios.eliminarProf(p.id!); await recargar(); }
    catch (e: any) { error = e.message; }
  }

  async function generarReporte() {
    error = null; reporte = null;
    try { reporte = await Consultorios.reporteIngresos(desde, hasta); }
    catch (e: any) { error = e.message; }
  }

  function centros(m: Record<string, number>): string {
    return Object.entries(m).map(([k,v]) => `${k}: Q${v}`).join(' · ');
  }

  // Liquidaciones
  let liquidaciones = $state<Liquidacion[]>([]);
  let liqConsultorioId = $state('');
  let info = $state<string|null>(null);

  async function cargarLiquidaciones() {
    try { liquidaciones = await Liquidaciones.list(); } catch (e: any) { error = e.message; }
  }
  async function generarLiquidaciones() {
    error = null; info = null;
    try {
      const r = await Liquidaciones.generar(desde, hasta, liqConsultorioId || undefined);
      info = r.length ? `${r.length} liquidación(es) generada(s).` : 'Sin consultorios arrendados con ingresos en el período.';
      await cargarLiquidaciones();
    } catch (e: any) { error = e.message; }
  }
  async function pagarLiq(l: Liquidacion) {
    try { await Liquidaciones.pagar(l.id!); await cargarLiquidaciones(); } catch (e: any) { error = e.message; }
  }
  async function emitirLiq(l: Liquidacion) {
    try { await Liquidaciones.emitir(l.id!); await cargarLiquidaciones(); } catch (e: any) { error = e.message; }
  }

  // Tarifario interno
  let tarifas = $state<Tarifa[]>([]);
  let nuevaTarifa = $state<Tarifa>({ codigo:'', descripcion:'', centroIngreso:'', precioPublico:0, precioInterno:null, activo:true });

  async function cargarTarifas() {
    try { tarifas = await Tarifas.list(false); } catch (e: any) { error = e.message; }
  }
  async function crearTarifa() {
    error = null;
    if (!nuevaTarifa.codigo || !nuevaTarifa.descripcion) { error = 'Código y descripción de la tarifa son requeridos'; return; }
    try {
      await Tarifas.crear({ ...nuevaTarifa, centroIngreso: nuevaTarifa.centroIngreso || null });
      nuevaTarifa = { codigo:'', descripcion:'', centroIngreso:'', precioPublico:0, precioInterno:null, activo:true };
      await cargarTarifas();
    } catch (e: any) { error = e.message; }
  }
  async function toggleTarifa(t: Tarifa) {
    try { await Tarifas.actualizar(t.id!, { ...t, activo: !t.activo }); await cargarTarifas(); }
    catch (e: any) { error = e.message; }
  }

  // Tarifas por convenio
  let aseguradoras = $state<Aseguradora[]>([]);
  let convenios = $state<TarifaConvenio[]>([]);
  let nuevoConvenio = $state<TarifaConvenio>({ codigo:'', aseguradoraId:'', precioConvenio:0, activo:true });

  async function cargarConvenios() {
    try {
      aseguradoras = await Seguros.list(false);
      convenios = await Tarifas.convenios();
    } catch (e: any) { error = e.message; }
  }
  async function crearConvenio() {
    error = null;
    if (!nuevoConvenio.codigo || !nuevoConvenio.aseguradoraId) { error = 'Código y aseguradora del convenio son requeridos'; return; }
    try {
      await Tarifas.crearConvenio({ ...nuevoConvenio });
      nuevoConvenio = { codigo:'', aseguradoraId:'', precioConvenio:0, activo:true };
      await cargarConvenios();
    } catch (e: any) { error = e.message; }
  }
  async function toggleConvenio(c: TarifaConvenio) {
    try { await Tarifas.actualizarConvenio(c.id!, { ...c, activo: !c.activo }); await cargarConvenios(); }
    catch (e: any) { error = e.message; }
  }
  function nombreAseguradora(id: string): string {
    return aseguradoras.find(a => a.id === id)?.nombre ?? id;
  }

  // Estado de cuenta de consultorios
  let estadoCuenta = $state<ReporteEstadoCuenta|null>(null);
  async function generarEstadoCuenta() {
    error = null; estadoCuenta = null;
    try { estadoCuenta = await Consultorios.estadoCuenta(desde, hasta); }
    catch (e: any) { error = e.message; }
  }

  // Cuentas por cobrar a consultorios
  let cuentasCons = $state<CuentaConsultorio[]>([]);
  async function cargarCuentasCons() {
    try { cuentasCons = await CuentasConsultorio.list(); } catch (e: any) { error = e.message; }
  }
  async function abonar(c: CuentaConsultorio) {
    error = null;
    const txt = prompt(`Abono para ${c.consultorioNombre ?? 'consultorio'} (saldo Q${c.saldo}):`, String(c.saldo));
    if (txt == null) return;
    const monto = Number(txt);
    if (!Number.isFinite(monto) || monto <= 0) { error = 'Monto de abono inválido'; return; }
    try { await CuentasConsultorio.abonar(c.consultorioId, { monto }); await cargarCuentasCons(); }
    catch (e: any) { error = e.message; }
  }

  // Modelo B · acuerdos con clínicas externas (slice B1)
  let acuerdos = $state<AcuerdoClinica[]>([]);
  let entrantes = $state<AcuerdoClinica[]>([]);
  let acuerdoConsultorioId = $state('');
  async function cargarAcuerdos() {
    try { acuerdos = await AcuerdosClinica.list(); entrantes = await AcuerdosClinica.entrantes(); }
    catch (e: any) { error = e.message; }
  }
  async function proponerAcuerdo() {
    error = null;
    if (!acuerdoConsultorioId) { error = 'Selecciona un consultorio externo'; return; }
    try { await AcuerdosClinica.proponer({ consultorioId: acuerdoConsultorioId }); acuerdoConsultorioId = ''; await cargarAcuerdos(); }
    catch (e: any) { error = e.message; }
  }
  async function suspenderAcuerdo(a: AcuerdoClinica) {
    try { await AcuerdosClinica.suspender(a.id); await cargarAcuerdos(); } catch (e: any) { error = e.message; }
  }
  async function terminarAcuerdo(a: AcuerdoClinica) {
    try { await AcuerdosClinica.terminar(a.id); await cargarAcuerdos(); } catch (e: any) { error = e.message; }
  }
  async function aceptarEntrante(a: AcuerdoClinica) {
    try { await AcuerdosClinica.aceptar(a.id); await cargarAcuerdos(); } catch (e: any) { error = e.message; }
  }
  async function rechazarEntrante(a: AcuerdoClinica) {
    try { await AcuerdosClinica.rechazar(a.id); await cargarAcuerdos(); } catch (e: any) { error = e.message; }
  }

  // Modelo B · estado de cuenta como clínica (con hospitales aliados)
  let estadoCuentaClinica = $state<EstadoCuentaCompartido[]>([]);
  async function cargarEstadoCuentaClinica() {
    try { estadoCuentaClinica = await Compartido.estadoCuenta(); } catch (e: any) { error = e.message; }
  }

  $effect(() => { recargar(); cargarLiquidaciones(); cargarTarifas(); cargarConvenios(); cargarCuentasCons(); cargarAcuerdos(); cargarEstadoCuentaClinica(); });
</script>

<section>
  <header class="view-header">
    <div>
      <h2>Consultorios / clínicas asociadas</h2>
      <p class="muted">Unidades de negocio del hospital: consultorios, profesionales, tarifas, liquidaciones y acuerdos.</p>
    </div>
  </header>

  {#if error}<div class="err">⚠ {error}</div>{/if}
  {#if info}<div class="ok">{info}</div>{/if}

  <nav class="tabs">
    <button class:active={tab === 'consultorios'} onclick={() => tab = 'consultorios'}>Consultorios</button>
    <button class:active={tab === 'profesionales'} onclick={() => tab = 'profesionales'}>Profesionales</button>
    <button class:active={tab === 'tarifario'} onclick={() => tab = 'tarifario'}>Tarifario</button>
    <button class:active={tab === 'liquidaciones'} onclick={() => tab = 'liquidaciones'}>Liquidaciones</button>
    <button class:active={tab === 'cuentas'} onclick={() => tab = 'cuentas'}>Cuentas por cobrar</button>
    <button class:active={tab === 'reportes'} onclick={() => tab = 'reportes'}>Reportes</button>
    <button class:active={tab === 'acuerdos'} onclick={() => tab = 'acuerdos'}>Acuerdos (B)</button>
  </nav>

  {#if tab === 'consultorios'}
    <div class="panel">
      <h3>{editandoConsId ? 'Editar consultorio' : 'Nuevo consultorio'}</h3>
      <p class="hint">Modela clínicas <strong>propias</strong> del hospital o espacios <strong>arrendados</strong> a médicos independientes (con comisión y/o renta).</p>
      <div class="form">
        <label class="field"><span>Código</span><input placeholder="CE-01" bind:value={nuevoCons.codigo} /></label>
        <label class="field"><span>Nombre</span><input placeholder="Nombre del consultorio" bind:value={nuevoCons.nombre} /></label>
        <label class="field"><span>Tipo</span>
          <select bind:value={nuevoCons.tipo}>
            <option value="PROPIO">Propio</option>
            <option value="ARRENDADO">Arrendado</option>
          </select>
        </label>
        <label class="field"><span>Vínculo</span>
          <select bind:value={nuevoCons.vinculo}>
            <option value="INTERNO">Interno (datos en el hospital)</option>
            <option value="EXTERNO">Externo (clínica con tenant propio)</option>
          </select>
        </label>
        {#if nuevoCons.vinculo === 'EXTERNO'}
          <label class="field"><span>Tenant externo (ID)</span>
            <input placeholder="tenant de la clínica" bind:value={nuevoCons.tenantExternoId} />
          </label>
        {/if}
        {#if nuevoCons.tipo === 'ARRENDADO'}
          <label class="field"><span>Modelo</span>
            <select bind:value={nuevoCons.modeloCobro}>
              <option value="COMISION">Comisión</option>
              <option value="RENTA_FIJA">Renta fija</option>
              <option value="MIXTO">Mixto</option>
            </select>
          </label>
          <label class="field"><span>% comisión hospital</span>
            <input type="number" min="0" max="100" step="0.01" bind:value={nuevoCons.comisionHospitalPct} />
          </label>
          <label class="field"><span>Renta mensual (Q)</span>
            <input type="number" min="0" step="0.01" bind:value={nuevoCons.rentaMensual} />
          </label>
        {/if}
        <label class="field"><span>Médico responsable</span>
          <select bind:value={nuevoCons.medicoResponsableId}>
            <option value={undefined}>— ninguno —</option>
            {#each profesionales as p (p.id)}<option value={p.id}>{p.nombre}</option>{/each}
          </select>
        </label>
        <button onclick={guardarConsultorio}>{editandoConsId ? 'Guardar cambios' : 'Crear consultorio'}</button>
        {#if editandoConsId}<button type="button" class="ghost" onclick={cancelarConsultorio}>Cancelar</button>{/if}
      </div>
    </div>

    <h3>Consultorios</h3>
    <table>
      <thead><tr><th>Código</th><th>Nombre</th><th>Tipo</th><th>Vínculo</th><th>Modelo</th><th>% com.</th><th>Renta</th><th></th></tr></thead>
      <tbody>
        {#each consultorios as c (c.id)}
          <tr>
            <td>{c.codigo}</td>
            <td>{c.nombre}</td>
            <td>{c.tipo}</td>
            <td>{c.vinculo === 'EXTERNO' ? `Externo${c.tenantExternoId ? ` (${c.tenantExternoId})` : ''}` : 'Interno'}</td>
            <td>{c.tipo === 'ARRENDADO' ? (c.modeloCobro ?? '—') : '—'}</td>
            <td>{c.tipo === 'ARRENDADO' ? `${c.comisionHospitalPct ?? 0}%` : '—'}</td>
            <td>{c.tipo === 'ARRENDADO' ? `Q${c.rentaMensual ?? 0}` : '—'}</td>
            <td><button onclick={() => editarConsultorio(c)}>Editar</button> <button onclick={() => eliminarConsultorio(c)}>Eliminar</button></td>
          </tr>
        {/each}
        {#if consultorios.length === 0 && !cargando}<tr><td colspan="8">Sin consultorios.</td></tr>{/if}
      </tbody>
    </table>
  {/if}

  {#if tab === 'profesionales'}
    <div class="panel">
      <h3>{editandoProfId ? 'Editar profesional' : 'Nuevo profesional'}</h3>
      <p class="hint">Médicos y profesionales que ejercen en los consultorios del hospital.</p>
      <div class="form">
        <label class="field"><span>Nombre</span><input placeholder="Nombre completo" bind:value={nuevoProf.nombre} /></label>
        <label class="field"><span>Colegiado</span><input placeholder="Colegiado" bind:value={nuevoProf.colegiado} /></label>
        <label class="field"><span>NIT</span><input placeholder="NIT" bind:value={nuevoProf.nit} /></label>
        <label class="field"><span>Relación</span>
          <select bind:value={nuevoProf.tipoRelacion}>
            <option value="EMPLEADO">Empleado</option>
            <option value="INDEPENDIENTE">Independiente</option>
          </select>
        </label>
        <label class="field"><span>% honorario médico</span>
          <input type="number" min="0" max="100" step="0.01" bind:value={nuevoProf.honorarioPct} />
        </label>
        <button onclick={guardarProfesional}>{editandoProfId ? 'Guardar cambios' : 'Crear profesional'}</button>
        {#if editandoProfId}<button type="button" class="ghost" onclick={cancelarProfesional}>Cancelar</button>{/if}
      </div>
    </div>

    <h3>Profesionales</h3>
    <table>
      <thead><tr><th>Nombre</th><th>Colegiado</th><th>Relación</th><th>% honorario</th><th></th></tr></thead>
      <tbody>
        {#each profesionales as p (p.id)}
          <tr>
            <td>{p.nombre}</td>
            <td>{p.colegiado ?? '—'}</td>
            <td>{p.tipoRelacion}</td>
            <td>{p.honorarioPct ?? 100}%</td>
            <td><button onclick={() => editarProfesional(p)}>Editar</button> <button onclick={() => eliminarProfesional(p)}>Eliminar</button></td>
          </tr>
        {/each}
        {#if profesionales.length === 0 && !cargando}<tr><td colspan="5">Sin profesionales.</td></tr>{/if}
      </tbody>
    </table>
  {/if}

  {#if tab === 'tarifario'}
    <div class="panel">
      <h3>Tarifario interno</h3>
      <p class="hint">Define el precio <strong>público</strong> y el <strong>interno</strong> (preferencial) por servicio. El interno se aplica a los renglones cuyo pagador es el <strong>consultorio</strong>.</p>
      <div class="form">
        <label class="field"><span>Código</span><input placeholder="CE-GEN" bind:value={nuevaTarifa.codigo} /></label>
        <label class="field"><span>Descripción</span><input placeholder="Descripción del servicio" bind:value={nuevaTarifa.descripcion} /></label>
        <label class="field"><span>Centro</span>
          <select bind:value={nuevaTarifa.centroIngreso}>
            <option value="">—</option>
            <option value="LABORATORIO">Laboratorio</option>
            <option value="IMAGENOLOGIA">Imagenología</option>
            <option value="HONORARIO">Honorario</option>
            <option value="FARMACIA">Farmacia</option>
            <option value="OTROS">Otros</option>
          </select>
        </label>
        <label class="field"><span>Precio público (Q)</span>
          <input type="number" min="0" step="0.01" bind:value={nuevaTarifa.precioPublico} />
        </label>
        <label class="field"><span>Precio interno (Q)</span>
          <input type="number" min="0" step="0.01" bind:value={nuevaTarifa.precioInterno} />
        </label>
        <button onclick={crearTarifa}>Crear tarifa</button>
      </div>
    </div>
    <table>
      <thead><tr><th>Código</th><th>Descripción</th><th>Centro</th><th>Público</th><th>Interno</th><th>Activo</th><th></th></tr></thead>
      <tbody>
        {#each tarifas as t (t.id)}
          <tr class:inactiva={!t.activo}>
            <td>{t.codigo}</td>
            <td>{t.descripcion}</td>
            <td>{t.centroIngreso ?? '—'}</td>
            <td>Q{t.precioPublico}</td>
            <td>{t.precioInterno != null ? `Q${t.precioInterno}` : '—'}</td>
            <td>{t.activo ? 'Sí' : 'No'}</td>
            <td><button onclick={() => toggleTarifa(t)}>{t.activo ? 'Desactivar' : 'Activar'}</button></td>
          </tr>
        {/each}
        {#if tarifas.length === 0}<tr><td colspan="7">Sin tarifas.</td></tr>{/if}
      </tbody>
    </table>

    <div class="panel">
      <h3>Tarifas por convenio (aseguradoras)</h3>
      <p class="hint">Precio pactado de un servicio con una aseguradora. Se aplica al renglón cuando el pagador es <strong>aseguradora</strong> y la cuenta la tiene asociada.</p>
      <div class="form">
        <label class="field"><span>Código servicio</span><input placeholder="Código" bind:value={nuevoConvenio.codigo} /></label>
        <label class="field"><span>Aseguradora</span>
          <select bind:value={nuevoConvenio.aseguradoraId}>
            <option value="">— elegir —</option>
            {#each aseguradoras as a (a.id)}<option value={a.id}>{a.nombre}</option>{/each}
          </select>
        </label>
        <label class="field"><span>Precio convenio (Q)</span>
          <input type="number" min="0" step="0.01" bind:value={nuevoConvenio.precioConvenio} />
        </label>
        <button onclick={crearConvenio}>Crear convenio</button>
      </div>
    </div>
    <table>
      <thead><tr><th>Código</th><th>Aseguradora</th><th>Precio convenio</th><th>Activo</th><th></th></tr></thead>
      <tbody>
        {#each convenios as c (c.id)}
          <tr class:inactiva={!c.activo}>
            <td>{c.codigo}</td>
            <td>{nombreAseguradora(c.aseguradoraId)}</td>
            <td>Q{c.precioConvenio}</td>
            <td>{c.activo ? 'Sí' : 'No'}</td>
            <td><button onclick={() => toggleConvenio(c)}>{c.activo ? 'Desactivar' : 'Activar'}</button></td>
          </tr>
        {/each}
        {#if convenios.length === 0}<tr><td colspan="5">Sin convenios.</td></tr>{/if}
      </tbody>
    </table>
  {/if}

  {#if tab === 'liquidaciones'}
    <div class="panel">
      <h3>Generar liquidaciones</h3>
      <p class="hint">Usa el período (Desde/Hasta). El hospital retiene comisión y/o renta; el resto es el neto del médico. Los consultorios propios no generan liquidación.</p>
      <div class="form">
        <label class="field"><span>Desde</span><input type="date" bind:value={desde} /></label>
        <label class="field"><span>Hasta</span><input type="date" bind:value={hasta} /></label>
        <label class="field"><span>Consultorio</span>
          <select bind:value={liqConsultorioId}>
            <option value="">Todos los arrendados</option>
            {#each consultorios.filter(c => c.tipo === 'ARRENDADO') as c (c.id)}<option value={c.id}>{c.nombre}</option>{/each}
          </select>
        </label>
        <button onclick={generarLiquidaciones}>Generar liquidaciones del período</button>
      </div>
    </div>

    <h3>Liquidaciones (consultorios arrendados)</h3>
    <table>
      <thead><tr><th>Consultorio</th><th>Período</th><th>Ingresos</th><th>Comisión</th><th>Renta</th><th>Retención</th><th>Neto médico</th><th>Neteo</th><th>A pagar</th><th>Estado</th><th></th></tr></thead>
      <tbody>
        {#each liquidaciones as l (l.id)}
          <tr>
            <td>{l.consultorioNombre ?? '—'}</td>
            <td>{l.desde} → {l.hasta}</td>
            <td>Q{l.ingresosTotales}</td>
            <td>Q{l.comisionHospital}</td>
            <td>Q{l.renta}</td>
            <td>Q{l.retencionHospital}</td>
            <td>Q{l.netoMedico}</td>
            <td>{(l.neteoConsultorio ?? 0) > 0 ? `−Q${l.neteoConsultorio}` : '—'}</td>
            <td><strong>Q{l.netoPagar ?? l.netoMedico}</strong></td>
            <td>{l.estado}</td>
            <td>
              {#if l.estado === 'BORRADOR'}<button onclick={() => emitirLiq(l)}>Emitir</button>{/if}
              {#if l.estado === 'BORRADOR' || l.estado === 'EMITIDA'}<button onclick={() => pagarLiq(l)}>Pagar</button>{/if}
            </td>
          </tr>
        {/each}
        {#if liquidaciones.length === 0}<tr><td colspan="11">Sin liquidaciones.</td></tr>{/if}
      </tbody>
    </table>
  {/if}

  {#if tab === 'cuentas'}
    <h3>Cuentas por cobrar a consultorios</h3>
    <p class="hint">Saldo acumulado de servicios consumidos por cada consultorio (pagador = consultorio). Los cargos se generan automáticamente; aquí registras los abonos.</p>
    <table>
      <thead><tr><th>Consultorio</th><th>Cargos</th><th>Abonos</th><th>Saldo</th><th>Estado</th><th></th></tr></thead>
      <tbody>
        {#each cuentasCons as c (c.id)}
          <tr class:inactiva={c.estado === 'SALDADA'}>
            <td>{c.consultorioNombre ?? '—'}</td>
            <td>Q{c.totalCargos}</td>
            <td>Q{c.totalAbonos}</td>
            <td><strong>Q{c.saldo}</strong></td>
            <td>{c.estado}</td>
            <td>{#if c.saldo > 0}<button onclick={() => abonar(c)}>Registrar abono</button>{/if}</td>
          </tr>
        {/each}
        {#if cuentasCons.length === 0}<tr><td colspan="6">Sin cuentas de consultorio.</td></tr>{/if}
      </tbody>
    </table>

    <div class="panel">
      <h3>Estado de cuenta de consultorios</h3>
      <p class="hint">Lo que cada consultorio <strong>debe al hospital</strong> por servicios que consumió y paga él mismo (pagador = consultorio), en el período.</p>
      <div class="form">
        <button onclick={generarEstadoCuenta}>Generar estado de cuenta</button>
      </div>
    </div>
    {#if estadoCuenta}
      <div class="resumen">
        <span>Total adeudado por consultorios: <strong>Q{estadoCuenta.totalAdeudado}</strong></span>
        {#if estadoCuenta.sinConsultorio > 0}<span>Sin consultorio asignado: <strong>Q{estadoCuenta.sinConsultorio}</strong></span>{/if}
      </div>
      <table>
        <thead><tr><th>Consultorio</th><th>Renglones</th><th>Total adeudado</th><th>Por centro</th></tr></thead>
        <tbody>
          {#each estadoCuenta.consultorios as r (r.consultorioId)}
            <tr>
              <td>{r.nombre}</td>
              <td>{r.renglones}</td>
              <td><strong>Q{r.totalAdeudado}</strong></td>
              <td>{centros(r.porCentroIngreso)}</td>
            </tr>
          {/each}
          {#if estadoCuenta.consultorios.length === 0}<tr><td colspan="4">Sin cargos a consultorios en el período.</td></tr>{/if}
        </tbody>
      </table>
    {/if}
  {/if}

  {#if tab === 'reportes'}
    <div class="panel">
      <h3>Reporte · ingresos por consultorio</h3>
      <p class="hint">Atribución de ingresos por consultorio en el período seleccionado.</p>
      <div class="form">
        <label class="field"><span>Desde</span><input type="date" bind:value={desde} /></label>
        <label class="field"><span>Hasta</span><input type="date" bind:value={hasta} /></label>
        <button onclick={generarReporte}>Generar</button>
      </div>
    </div>

    {#if reporte}
      <div class="resumen">
        <span>Total ingresos: <strong>Q{reporte.totalIngresos}</strong></span>
        <span>Comisión estimada hospital: <strong>Q{reporte.comisionHospitalEstimada}</strong></span>
      </div>
      <table>
        <thead><tr><th>Consultorio</th><th>Tipo</th><th>Renglones</th><th>Ingresos</th><th>% com.</th><th>Comisión hosp.</th><th>Por centro</th></tr></thead>
        <tbody>
          {#each reporte.consultorios as r (r.consultorioId)}
            <tr>
              <td>{r.nombre}</td>
              <td>{r.tipo ?? '—'}</td>
              <td>{r.renglones}</td>
              <td>Q{r.totalIngresos}</td>
              <td>{r.comisionHospitalPct}%</td>
              <td>Q{r.comisionHospitalEstimada}</td>
              <td>{centros(r.porCentroIngreso)}</td>
            </tr>
          {/each}
          {#if reporte.consultorios.length === 0}<tr><td colspan="7">Sin ingresos atribuidos en el período.</td></tr>{/if}
        </tbody>
      </table>
    {/if}
  {/if}

  {#if tab === 'acuerdos'}
    <div class="panel">
      <h3>Acuerdos con clínicas externas (Modelo B)</h3>
      <p class="hint">Una clínica externa es una <strong>organización con su propio tenant</strong>. El hospital propone un acuerdo sobre un consultorio con vínculo <strong>Externo</strong>; la clínica lo acepta desde su propia cuenta.</p>
      <div class="form">
        <label class="field"><span>Consultorio externo</span>
          <select bind:value={acuerdoConsultorioId}>
            <option value="">— selecciona —</option>
            {#each consultorios.filter(c => c.vinculo === 'EXTERNO') as c (c.id)}<option value={c.id}>{c.nombre}</option>{/each}
          </select>
        </label>
        <button onclick={proponerAcuerdo}>Proponer acuerdo</button>
      </div>
    </div>
    <table>
      <thead><tr><th>Clínica</th><th>Tenant clínica</th><th>Comparte</th><th>Estado</th><th></th></tr></thead>
      <tbody>
        {#each acuerdos as a (a.id)}
          <tr>
            <td>{a.clinicaNombre ?? '—'}</td>
            <td><code>{a.clinicaTenantId}</code></td>
            <td>{[a.comparteResultadosLab && 'Lab', a.comparteResultadosImagen && 'Imagen', a.comparteEstadoCuenta && 'Edo. cuenta'].filter(Boolean).join(', ') || '—'}</td>
            <td>{a.estado}</td>
            <td>
              {#if a.estado !== 'TERMINADO'}<button onclick={() => suspenderAcuerdo(a)}>Suspender</button>{/if}
              {#if a.estado !== 'TERMINADO'}<button onclick={() => terminarAcuerdo(a)}>Terminar</button>{/if}
            </td>
          </tr>
        {/each}
        {#if acuerdos.length === 0}<tr><td colspan="5">Sin acuerdos propuestos. Crea un consultorio con vínculo Externo y propón uno.</td></tr>{/if}
      </tbody>
    </table>

    <h3>Acuerdos entrantes (cuando este tenant es la clínica)</h3>
    <p class="hint">Acuerdos donde otra organización (un hospital) te propone una relación. Acéptalos para habilitar la compartición.</p>
    <table>
      <thead><tr><th>Propuesto por (tenant)</th><th>Comparte</th><th>Estado</th><th></th></tr></thead>
      <tbody>
        {#each entrantes as a (a.id)}
          <tr>
            <td><code>{a.tenantId ?? a.clinicaNombre ?? '—'}</code></td>
            <td>{[a.comparteResultadosLab && 'Lab', a.comparteResultadosImagen && 'Imagen', a.comparteEstadoCuenta && 'Edo. cuenta'].filter(Boolean).join(', ') || '—'}</td>
            <td>{a.estado}</td>
            <td>
              {#if a.estado !== 'ACEPTADO' && a.estado !== 'TERMINADO'}<button onclick={() => aceptarEntrante(a)}>Aceptar</button>{/if}
              {#if a.estado !== 'TERMINADO'}<button onclick={() => rechazarEntrante(a)}>Rechazar</button>{/if}
            </td>
          </tr>
        {/each}
        {#if entrantes.length === 0}<tr><td colspan="4">Sin acuerdos entrantes.</td></tr>{/if}
      </tbody>
    </table>

    <h3>Mi estado de cuenta como clínica (con hospitales aliados)</h3>
    <p class="hint">Si este tenant es una clínica con un acuerdo <strong>aceptado</strong>, aquí ve lo que le debe a cada hospital aliado (solo-lectura).</p>
    <table>
      <thead><tr><th>Hospital (tenant)</th><th>Cargos</th><th>Abonos</th><th>Saldo</th><th>Estado</th></tr></thead>
      <tbody>
        {#each estadoCuentaClinica as e (e.consultorioId)}
          <tr>
            <td><code>{e.hospitalTenantId}</code></td>
            <td>Q{e.totalCargos}</td>
            <td>Q{e.totalAbonos}</td>
            <td><strong>Q{e.saldo}</strong></td>
            <td>{e.estadoCuenta}</td>
          </tr>
        {/each}
        {#if estadoCuentaClinica.length === 0}<tr><td colspan="5">Sin estado de cuenta con hospitales aliados.</td></tr>{/if}
      </tbody>
    </table>
  {/if}
</section>

<style>
  section { padding: 1rem; }
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  .tabs { display: flex; gap: .35rem; flex-wrap: wrap; margin: .8rem 0 1.2rem; border-bottom: 1px solid #e5e7eb; padding-bottom: .5rem; }
  .tabs button { padding: .5rem .95rem; border: 1px solid #e5e7eb; background: #fff; color: #374151; border-radius: 8px 8px 0 0; cursor: pointer; font: inherit; font-weight: 600; font-size: .85rem; box-shadow: none; }
  .tabs button:hover { background: #f0fdf4; color: #16a34a; transform: none; }
  .tabs button.active { background: #16a34a; border-color: #16a34a; color: #fff; }
  h2 { margin: 0 0 0.2rem; font-size: 1.4rem; color: #111827; }
  h3 { margin: 1rem 0 .5rem; font-size: 1rem; color: #166534; }
  .hint { color: #6b7280; font-size: .88rem; max-width: 62rem; margin: .2rem 0 .6rem; }
  .err { background: #fee2e2; color: #dc2626; padding: .6rem .8rem; border-radius: 8px; margin: .5rem 0; }
  .ok { background: #dcfce7; color: #166534; padding: .6rem .8rem; border-radius: 8px; margin: .5rem 0; }
  .panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1.25rem; margin: .6rem 0 1rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .form { display: flex; gap: .75rem; flex-wrap: wrap; align-items: flex-end; margin: .6rem 0; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .form input, .form select { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827; }
  .form input:focus, .form select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover { transform: translateY(-1px); }
button.ghost { background: #fff; color: #475569; border: 1px solid #e5e7eb; box-shadow: none; }
button.ghost:hover { transform: none; background: #f1f5f9; }
  td button { padding: .25rem .55rem; font-size: .8rem; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; color: #16a34a; box-shadow: none; font-weight: 500; }
  td button:hover { background: #f0fdf4; transform: none; }
  table { width: 100%; border-collapse: collapse; margin-top: .6rem; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { border-top: 1px solid #f3f4f6; padding: .4rem .8rem; text-align: left; color: #111827; }
  tr.inactiva { color: #9ca3af; }
  .resumen { display: flex; gap: 1.5rem; flex-wrap: wrap; background: #f0fdf4; border-left: 4px solid #16a34a; padding: .6rem 1rem; margin-top: .8rem; border-radius: 8px; }
  .resumen strong { color: #166534; }
  code { background: #f3f4f6; padding: .1rem .35rem; border-radius: 4px; font-size: .8rem; color: #111827; }
</style>
